import React, { useEffect, useState } from "react";
import { GAME_META, getMatches, getStreaming } from "@/lib/api";
import Bracket from "@/components/Bracket";
import StreamingPanel from "@/components/StreamingPanel";
import { motion } from "framer-motion";

export default function GameSection({ gameKey, extraInfo = null }) {
  const meta = GAME_META[gameKey];
  const [matches, setMatches] = useState([]);
  const [streaming, setStreaming] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [m, s] = await Promise.all([
          getMatches(gameKey),
          getStreaming(gameKey),
        ]);
        if (!cancelled) {
          setMatches(m);
          setStreaming(s);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [gameKey]);

  return (
    <section
      data-testid={`game-section-${gameKey}`}
      className="border-t border-white/5 pt-12 first:border-t-0 first:pt-0"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
      >
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.3em]"
            style={{ color: meta.accent }}
          >
            {meta.platform} · Jogo
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-wider leading-none mt-1">
            {meta.name}
          </h2>
          {meta.format && (
            <div
              data-testid={`game-format-${gameKey}`}
              className="inline-flex items-center gap-2 mt-3 border px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-semibold"
              style={{ color: meta.accent, borderColor: `${meta.accent}66` }}
            >
              <span className="h-1.5 w-1.5" style={{ backgroundColor: meta.accent }} />
              {meta.format}
            </div>
          )}
          <p className="text-sm text-white/60 mt-3 max-w-2xl">
            {meta.description}
          </p>
        </div>
      </motion.div>

      {extraInfo && (
        <div className="mb-8 grid md:grid-cols-2 gap-4">{extraInfo}</div>
      )}

      <div className="grid gap-8">
        <StreamingPanel
          streaming={streaming}
          gameName={meta.name}
          accent={meta.accent}
        />

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl uppercase tracking-wider">
              Quadro do Torneio
            </h3>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              {matches.length} partidas
            </div>
          </div>
          {loading ? (
            <div
              data-testid="bracket-loading"
              className="h-48 border border-white/10 bg-[#141414] grid place-items-center text-white/50 uppercase tracking-wider text-sm"
            >
              A carregar...
            </div>
          ) : (
            <Bracket matches={matches} accent={meta.accent} />
          )}
        </div>
      </div>
    </section>
  );
}
